import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { stats, bookings as bookingsApi } from '../api';
import { FiPackage, FiCalendar, FiCheckCircle, FiClock, FiGlobe } from 'react-icons/fi';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { positionsLabel, piecesLabel, bookingsLabel } from '../utils/pluralRu';
import { PRICE_COST_WITHOUT_VAT } from '../constants/equipmentLabels';
import EquipmentComment from '../components/EquipmentComment';

function Dashboard() {
  const [statsData, setStatsData] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        stats.get(),
        bookingsApi.getAll({ limit: 5 })
      ]);
      setStatsData(statsRes);
      setRecentBookings(bookingsRes);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Панель управления</h1>
        <p className="mt-2 text-sm text-gray-600">
          Обзор склада и бронирований
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiPackage className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Всего оборудования
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {statsData?.total_equipment || 0}
                  </dd>
                  <dd className="text-sm text-gray-500 mt-1">
                    {positionsLabel(statsData?.total_equipment || 0)} ·{' '}
                    {statsData?.total_quantity || 0} {piecesLabel(statsData?.total_quantity || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiCheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Доступно к бронированию
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {statsData?.available_equipment || 0}
                  </dd>
                  <dd className="text-sm text-gray-500 mt-1">
                    {positionsLabel(statsData?.available_equipment || 0)} ·{' '}
                    {statsData?.available_quantity || 0} {piecesLabel(statsData?.available_quantity || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiCalendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    В резерве (активные брони)
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {statsData?.reserved_equipment || 0}
                  </dd>
                  <dd className="text-sm text-gray-500 mt-1">
                    {positionsLabel(statsData?.reserved_equipment || 0)} ·{' '}
                    {statsData?.active_bookings_quantity || 0} {piecesLabel(statsData?.active_bookings_quantity || 0)}
                  </dd>
                  <dd className="text-xs text-gray-400 mt-1">
                    {statsData?.active_bookings || 0} {bookingsLabel(statsData?.active_bookings || 0)} всего · мои:{' '}
                    {statsData?.my_active_bookings || 0} ({statsData?.my_active_bookings_quantity || 0}{' '}
                    {piecesLabel(statsData?.my_active_bookings_quantity || 0)})
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiClock className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Всего бронирований
                  </dt>
                  <dd className="text-3xl font-semibold text-gray-900">
                    {statsData?.total_bookings || 0} / {statsData?.my_bookings || 0}
                  </dd>
                  <dd className="text-sm text-gray-500 mt-1">
                    все / мои
                  </dd>
                  <dd className="text-xs text-gray-400 mt-1" title="Учитываются все статусы: активные, завершённые и отменённые">
                    Все записи в системе и ваши личные (любой статус)
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
        <Link
          to="/equipment"
          className="bg-gradient-to-r from-primary-500 to-blue-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          <div className="text-white">
            <FiPackage className="h-8 w-8 mb-2" />
            <h3 className="text-xl font-semibold">Просмотр оборудования</h3>
            <p className="mt-2 text-sm opacity-90">
              Посмотрите доступное оборудование и забронируйте нужное
            </p>
          </div>
        </Link>

        <Link
          to="/partners"
          className="bg-gradient-to-r from-indigo-500 to-violet-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          <div className="text-white">
            <FiGlobe className="h-8 w-8 mb-2" />
            <h3 className="text-xl font-semibold">Партнёры</h3>
            <p className="mt-2 text-sm opacity-90">
              Справочник складов партнёров — только просмотр, без бронирования
            </p>
          </div>
        </Link>

        <Link
          to="/bookings"
          className="bg-gradient-to-r from-green-500 to-teal-600 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          <div className="text-white">
            <FiCalendar className="h-8 w-8 mb-2" />
            <h3 className="text-xl font-semibold">Бронирования</h3>
            <p className="mt-2 text-sm opacity-90">
              Управляйте своими бронированиями
            </p>
          </div>
        </Link>
      </div>

      {/* Recent Bookings */}
      {recentBookings.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Последние бронирования
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {recentBookings.map((booking) => (
              <div key={booking.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {booking.equipment.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {booking.equipment.vendor} • {booking.equipment.model}
                    </p>
                    {booking.equipment.code_1c && (
                      <p className="text-xs text-gray-500 mt-0.5 font-mono">
                        {booking.equipment.code_1c}
                      </p>
                    )}
                    {booking.equipment.price_ddp != null && booking.equipment.price_ddp !== '' && (
                      <p className="text-xs text-gray-700 mt-0.5">
                        {PRICE_COST_WITHOUT_VAT}: {Number(booking.equipment.price_ddp).toLocaleString('ru-RU')} ₽
                      </p>
                    )}
                    <EquipmentComment text={booking.equipment.comment} compact className="mt-1" />
                    <p className="text-xs text-gray-600 mt-1">
                      <span className="font-medium">Забронировал:</span> {booking.user.full_name}
                    </p>
                    {booking.client_name && (
                      <p className="text-xs text-gray-500 mt-1">
                        Клиент: {booking.client_name}
                      </p>
                    )}
                    {booking.deal_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Сделка:{' '}
                        {booking.deal_url ? (
                          <a
                            href={booking.deal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline"
                          >
                            #{booking.deal_id}
                          </a>
                        ) : (
                          `#${booking.deal_id}`
                        )}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-sm text-gray-900">
                      {format(new Date(booking.start_date), 'd MMM', { locale: ru })} -{' '}
                      {format(new Date(booking.end_date), 'd MMM', { locale: ru })}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        booking.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {booking.status === 'active'
                        ? 'Активно'
                        : booking.status === 'cancelled'
                        ? 'Отменено'
                        : booking.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
